import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Globe,
  GraduationCap,
  FileText,
  CheckCircle2,
  Building2,
  MapPin,
  Users,
  Plane,
  BookOpen,
  Award,
  Clock,
  ArrowRight,
  Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function StudentServicesPage() {
  const { token, isAuthenticated } = useAuth();
  const [universities, setUniversities] = useState([]);
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    university_name: '',
    program_name: '',
    intake: '',
    visa_required: false,
    visa_type: '',
    message: ''
  });

  useEffect(() => {
    fetchUniversities();
  }, []);

  const fetchUniversities = async () => {
    try {
      const response = await fetch(`${API_URL}/api/student-services/universities`);
      const data = await response.json();
      setUniversities(data);
    } catch (error) {
      setUniversities([]);
    }
  };

  const handleApply = (university) => {
    setSelectedProgram(university);
    setFormData({ ...formData, university_name: university.name, program_name: '' });
    setShowApplicationModal(true);
  };

  const submitApplication = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to submit an application');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/student-services/applications`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Application submitted successfully! Our team will contact you soon.');
        setShowApplicationModal(false);
        setFormData({
          university_name: '',
          program_name: '',
          intake: '',
          visa_required: false,
          visa_type: '',
          message: ''
        });
      } else {
        throw new Error('Failed to submit');
      }
    } catch (error) {
      toast.error('Failed to submit application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const services = [
    {
      icon: BookOpen,
      title: 'University Admissions',
      description: 'Expert guidance on university applications, personal statements, and course selection.'
    },
    {
      icon: Plane,
      title: 'Visa Assistance',
      description: 'Complete support for student visa applications including Tier 4 and Graduate visas.'
    },
    {
      icon: FileText,
      title: 'Document Preparation',
      description: 'Help with preparing all required documents, transcripts, and certifications.'
    },
    {
      icon: Users,
      title: 'Career Counseling',
      description: 'Guidance on career paths and post-graduation opportunities in the UK.'
    }
  ];

  const stats = [
    { value: '500+', label: 'Partner Universities' },
    { value: '10,000+', label: 'Students Placed' },
    { value: '95%', label: 'Visa Success Rate' },
    { value: '50+', label: 'Countries Served' },
  ];

  return (
    <div className="min-h-screen" data-testid="student-services-page">
      {/* Hero */}
      <section className="bg-gradient-to-r from-amber-500 to-orange-500 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <Badge className="bg-white/20 text-white border-white/30 mb-6">
              <Globe className="w-3 h-3 mr-1" />
              International Student Services
            </Badge>
            <h1 className="text-4xl sm:text-5xl font-bold mb-6">
              Your Gateway to UK Education
            </h1>
            <p className="text-xl text-white/90 mb-8">
              We help international students navigate the journey from application to graduation. 
              Expert visa assistance, university admissions, and career support.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button 
                className="bg-white text-amber-600 hover:bg-white/90 rounded-full px-8 h-12"
                onClick={() => setShowApplicationModal(true)}
                data-testid="apply-now-btn"
              >
                Apply Now
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Link to="/register?type=student">
                <Button variant="outline" className="border-white text-white hover:bg-white/10 rounded-full px-8 h-12">
                  Create Student Account
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-white py-12 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, idx) => (
              <div key={idx} className="text-center">
                <div className="text-3xl sm:text-4xl font-bold text-amber-600">{stat.value}</div>
                <div className="text-muted-foreground mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 bg-[#F0F4F8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Our Services</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Comprehensive support for your educational journey in the UK
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, idx) => (
              <Card key={idx} className="bg-white border-0 shadow-sm hover:shadow-lg transition-all">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center mb-4">
                    <service.icon className="w-6 h-6 text-amber-600" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">{service.title}</h3>
                  <p className="text-muted-foreground text-sm">{service.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Partner Universities */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Partner Universities</h2>
            <p className="text-muted-foreground">
              We work with leading UK universities to help you achieve your academic goals
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {universities.map((uni) => (
              <Card key={uni.id} className="hover:shadow-lg transition-all group" data-testid={`university-card-${uni.id}`}>
                <CardContent className="p-6">
                  <div className="w-14 h-14 rounded-xl bg-[#003366]/10 flex items-center justify-center mb-4 group-hover:bg-[#003366] transition-colors">
                    <GraduationCap className="w-7 h-7 text-[#003366] group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="font-semibold text-lg mb-1">{uni.name}</h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    <MapPin className="w-3 h-3 inline mr-1" />
                    {uni.country}
                  </p>
                  <div className="flex items-center justify-between">
                    <Badge variant="secondary">{uni.programs_count ?? (uni.programs?.length || 0)} programs</Badge>
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      className="text-amber-600 hover:text-amber-700"
                      onClick={() => handleApply(uni)}
                    >
                      Apply
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Visa Support */}
      <section className="py-16 bg-[#003366] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="bg-white/10 text-white border-white/20 mb-4">
                <Plane className="w-3 h-3 mr-1" />
                Visa Assistance
              </Badge>
              <h2 className="text-3xl font-bold mb-6">Complete Visa Support</h2>
              <p className="text-white/80 mb-6">
                Our experienced team has helped thousands of students secure their UK visas. 
                We guide you through every step of the process.
              </p>
              <ul className="space-y-4">
                {[
                  'Student Visa (Tier 4) Applications',
                  'Graduate Visa Support',
                  'Skilled Worker Visa Guidance',
                  'Document Preparation & Review',
                  'Interview Preparation',
                  'Post-visa Arrival Support'
                ].map((item, idx) => (
                  <li key={idx} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white/10 rounded-2xl p-8">
              <h3 className="text-xl font-bold mb-4">95% Visa Success Rate</h3>
              <p className="text-white/80 mb-6">
                Our track record speaks for itself. We ensure your visa application is 
                complete and compelling.
              </p>
              <Button 
                className="bg-amber-500 hover:bg-amber-600 text-white rounded-full px-8"
                onClick={() => setShowApplicationModal(true)}
              >
                Get Started
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#F0F4F8]">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Start Your Journey?</h2>
          <p className="text-muted-foreground mb-8">
            Book a free consultation with our education advisors today
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              className="bg-amber-500 hover:bg-amber-600 rounded-full px-8 h-12"
              onClick={() => setShowApplicationModal(true)}
            >
              Book Free Consultation
            </Button>
            <Link to="/contact">
              <Button variant="outline" className="rounded-full px-8 h-12">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Application Modal */}
      <Dialog open={showApplicationModal} onOpenChange={setShowApplicationModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Apply for Study Abroad</DialogTitle>
            <DialogDescription>
              Fill in your details and our team will contact you within 24 hours
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>University</Label>
              <Input
                value={formData.university_name}
                onChange={(e) => setFormData({ ...formData, university_name: e.target.value })}
                placeholder="Select or enter university name"
              />
            </div>
            <div className="space-y-2">
              <Label>Program / Course</Label>
              {selectedProgram?.programs?.length > 0 ? (
                <Select
                  value={formData.program_name}
                  onValueChange={(value) => setFormData({ ...formData, program_name: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a program" />
                  </SelectTrigger>
                  <SelectContent>
                    {selectedProgram.programs.map((p) => (
                      <SelectItem key={p.id || p.name} value={p.name}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  value={formData.program_name}
                  onChange={(e) => setFormData({ ...formData, program_name: e.target.value })}
                  placeholder="e.g., MSc Computer Science"
                />
              )}
            </div>
            <div className="space-y-2">
              <Label>Preferred Intake</Label>
              <Select 
                value={formData.intake} 
                onValueChange={(value) => setFormData({ ...formData, intake: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select intake" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="September 2025">September 2025</SelectItem>
                  <SelectItem value="January 2026">January 2026</SelectItem>
                  <SelectItem value="September 2026">September 2026</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Do you need visa assistance?</Label>
              <Select 
                value={formData.visa_required ? 'yes' : 'no'} 
                onValueChange={(value) => setFormData({ ...formData, visa_required: value === 'yes' })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">Yes, I need visa support</SelectItem>
                  <SelectItem value="no">No, I don't need visa support</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Additional Message (Optional)</Label>
              <Textarea
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                placeholder="Any specific questions or requirements?"
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApplicationModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={submitApplication}
              disabled={loading || !formData.university_name || !formData.program_name}
              className="bg-amber-500 hover:bg-amber-600"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Submit Application
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
